/**
 * Construye las tres piezas de infraestructura de la aplicación y las
 * conecta entre sí: el store de tokens, el cliente HTTP y el QueryClient.
 *
 * Existe como función —y no como módulo con instancias sueltas— para que un
 * test pueda armar un runtime completo con `fetch` falso y store en memoria,
 * y montar la app entera sin tocar `localStorage` ni la red.
 */
import { ApiClient, type SessionEndReason } from '@/api/client';
import type { ApiError } from '@/api/errors';
import { createQueryClient } from '@/api/queryClient';
import { createTokenStore, type TokenStore } from '@/auth/tokens';
import type { QueryClient } from '@tanstack/react-query';

export type SessionEndHandler = (reason: SessionEndReason, error: ApiError | null) => void;

export interface AppRuntime {
  tokens: TokenStore;
  api: ApiClient;
  queryClient: QueryClient;
  /**
   * El `fetch` que usa toda la aplicación, incluido lo que **no** pasa por el
   * `ApiClient` — hoy sólo el JSON de marca blanca del tenant, que es un
   * asset estático y no una ruta de la API.
   *
   * Está expuesto para que no quede ni un pedido a la red fuera del alcance
   * de un test: si algo se saltea esto, no se puede probar sin red.
   */
  fetchImpl: typeof fetch;
  /**
   * Registra al `AuthProvider` como oyente del fin de sesión. Devuelve el
   * desuscriptor. El `ApiClient` no puede llamar a React directamente: se
   * construye antes de que exista el árbol.
   */
  onSessionEnd: (handler: SessionEndHandler) => () => void;
}

export interface RuntimeOptions {
  baseUrl?: string;
  tokens?: TokenStore;
  fetchImpl?: typeof fetch;
  useWebLocks?: boolean;
}

export function createRuntime(options: RuntimeOptions = {}): AppRuntime {
  const handlers = new Set<SessionEndHandler>();
  const tokens = options.tokens ?? createTokenStore();
  // Ligado a `globalThis`: `fetch` desligado tira `Illegal invocation`.
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const api = new ApiClient({
    // Vacío = mismo origen. Ver `vite.config.ts` y `.env.example`.
    baseUrl: options.baseUrl ?? import.meta.env.VITE_API_BASE ?? '',
    tokens,
    onSessionEnded: (reason, error) => {
      for (const handler of handlers) handler(reason, error);
    },
    fetchImpl,
    ...(options.useWebLocks === undefined ? {} : { useWebLocks: options.useWebLocks }),
  });

  return {
    tokens,
    api,
    fetchImpl,
    queryClient: createQueryClient(),
    onSessionEnd: (handler) => {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
  };
}
